import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faSave } from "@fortawesome/pro-solid-svg-icons/faSave";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Drawer from "antd/es/drawer";
import List from "antd/es/list";
import Tag from "antd/es/tag";
import { compareAsc, compareDesc } from "date-fns/esm";
import { useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { EntityTypesSupportedByImport } from "@mapmycustomers/shared/types/entity";
import ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import { SearchField } from "@mapmycustomers/ui";

import { deleteConfig, selectConfig } from "@app/scene/settings/component/Imports/store/actions";
import {
  getEntityType,
  getImportConfigs,
} from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { getEntityTypeDisplayName } from "@app/util/ui";

import ConfigListItem from "./ConfigListItem";
import styles from "./ConfigListPanel.module.scss";
import ConfigSortOrderSelect from "./ConfigSortOrderSelect";

interface Props {
  analyticIssuer?: AnalyticsService;
  configs: ImportConfig[];
  deleteConfig: typeof deleteConfig.request;
  entityType: EntityTypesSupportedByImport;
  onHide: () => void;
  selectConfig: typeof selectConfig;
  visible: boolean;
}

const ConfigListPanel: React.FC<Props> = ({
  analyticIssuer,
  configs,
  deleteConfig,
  entityType,
  onHide,
  selectConfig,
  visible,
}) => {
  const intl = useIntl();
  const [search, setSearch] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  const configRows: ImportConfig[] = useMemo(() => {
    const query = search.trim().toLowerCase();
    const response = query
      ? configs.filter((config) => config.name.toLowerCase().includes(query))
      : [...configs];
    const configDateComparator = sortOrder === SortOrder.ASC ? compareAsc : compareDesc;
    return response.sort((config1, config2) =>
      configDateComparator(config1.updatedAt, config2.updatedAt)
    );
  }, [configs, search, sortOrder]);

  const handleSelectConfig = useCallback(
    (config: ImportConfig) => {
      selectConfig(config);
      onHide();
      analyticIssuer?.clicked(["Select Config"], { config });
    },
    [analyticIssuer, onHide, selectConfig]
  );

  const handleDeleteConfig = useCallback(
    (id: ImportConfig["id"]) => {
      deleteConfig(id);
      analyticIssuer?.clicked(["Delete Config"], { id });
    },
    [analyticIssuer, deleteConfig]
  );

  return (
    <Drawer
      className={styles.container}
      closable
      onClose={onHide}
      placement="right"
      title={
        <div className={styles.title}>
          <div className={styles.titleIconContainer}>
            <FontAwesomeIcon className={styles.titleIcon} icon={faSave} />
          </div>
          <div>
            {intl.formatMessage({
              id: "settings.newImports.config.configListPanel.title",
              defaultMessage: "Saved Import Matchings",
              description: "Imports config list pane title",
            })}
          </div>
          <Tag>
            {getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })}
          </Tag>
        </div>
      }
      visible={visible}
    >
      <div className={styles.actionBar}>
        <SearchField
          className={styles.searchField}
          onChange={setSearch}
          placeholder={intl.formatMessage({
            id: "settings.newImports.configListPanel.search.placeholder",
            defaultMessage: "Type to search",
            description: "Imports config list pane search placeholder",
          })}
        />
        <ConfigSortOrderSelect onChange={setSortOrder} sorting={sortOrder} />
      </div>
      <List<ImportConfig>
        dataSource={configRows}
        renderItem={(config) => (
          <ConfigListItem
            config={config}
            onDelete={handleDeleteConfig}
            onSelect={handleSelectConfig}
          />
        )}
      />
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  configs: getImportConfigs(state),
  entityType: getEntityType(state),
});

const mapDispatchToProps = {
  deleteConfig: deleteConfig.request,
  selectConfig,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigListPanel);
